import { useCallback } from "react";
import { Capacitor, CapacitorHttp as Http } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { auth } from "../helpers/auth";
import { useRateLimitDialog } from "../context/RateLimitContext";
import { useOfflineDialog } from "../context/OfflineContext";
import { isNetworkError as isOfflineError } from "../helpers/networkErrors";

const API_URL = Capacitor.isNativePlatform()
  ? "https://mindstack.app/api/"
  : "/api/";

// Retry configuration
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 8000;

/**
 * Calculate delay with exponential backoff and jitter
 * @param {number} attempt - Current retry attempt (0-indexed)
 * @returns {number} Delay in milliseconds
 */
const getRetryDelay = (attempt) => {
  const exponentialDelay = Math.min(
    BASE_DELAY_MS * Math.pow(2, attempt),
    MAX_DELAY_MS,
  );
  // Add jitter (random 0-25% of delay) to prevent thundering herd
  const jitter = Math.random() * 0.25 * exponentialDelay;
  return exponentialDelay + jitter;
};

/**
 * Determine if an error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean} Whether the request should be retried
 */
const isRetryableError = (error) => {
  // Network/offline errors
  if (isOfflineError(error)) {
    return true;
  }

  // Server errors (5xx) - server might be temporarily overloaded
  if (error.status >= 500 && error.status < 600) {
    return true;
  }

  // Request timeout
  if (error.status === 408) {
    return true;
  }

  // Rate limiting - we'll retry with backoff (WAF might be temporarily blocking)
  if (error.status === 429) {
    return true;
  }

  // WAF-specific: sometimes returns 403 for transient issues
  // Only retry 403 if it looks like a WAF block (no specific error message)
  if (
    error.status === 403 &&
    (!error.response?.data?.message ||
      error.response?.data?.message === "Request failed")
  ) {
    return true;
  }

  return false;
};

/**
 * Sleep for a given duration
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const useApiClient = () => {
  const { showRateLimitDialog } = useRateLimitDialog();
  const { showOfflineDialog } = useOfflineDialog();

  /**
   * Execute a single request attempt (without retry logic)
   */
  const executeRequest = useCallback(
    async (method, path, data, idToken) => {
      const url = `${API_URL}${path}`;
      const headers = {
        Authorization: `${idToken}`,
        "Content-Type": "application/json",
        Connection: "close",
      };

      if (Capacitor.isNativePlatform()) {
        const options = {
          url,
          method,
          headers,
        };

        if (data) {
          options.data = data;
        }

        const response = await Http.request(options);

        // Check for error status codes
        if (response.status >= 400) {
          // Show rate limit dialog if server returns 429 Too Many Requests
          if (response.status === 429) {
            showRateLimitDialog();
          }
          const error = new Error(response.data?.message || "Request failed");
          error.status = response.status;
          error.response = { status: response.status, data: response.data };
          error.errors = response.data?.errors;
          throw error;
        }

        return response.data;
      } else {
        const response = await fetch(url, {
          method,
          headers,
          ...(data && { body: JSON.stringify(data) }),
        });

        // Handle rate limit (429) before trying to parse response
        if (response.status === 429) {
          showRateLimitDialog();
          const error = new Error("Too many requests. Please try again later.");
          error.status = 429;
          error.response = { status: 429 };
          throw error;
        }

        // Try to parse JSON, but handle cases where response isn't valid JSON
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          // Response wasn't valid JSON (e.g., WAF error page)
          if (!response.ok) {
            const error = new Error("Request failed");
            error.status = response.status;
            error.response = { status: response.status };
            throw error;
          }
          throw parseError;
        }

        // Check for error status codes
        if (!response.ok) {
          const error = new Error(responseData?.message || "Request failed");
          error.status = response.status;
          error.response = { status: response.status, data: responseData };
          error.errors = responseData?.errors;
          throw error;
        }

        return responseData;
      }
    },
    [showRateLimitDialog],
  );

  /**
   * Make an API request with automatic retry and exponential backoff
   */
  const makeRequest = useCallback(
    async (method, path, data = null) => {
      // Get ID token first (outside retry loop)
      let idToken;
      try {
        if (Capacitor.isNativePlatform()) {
          idToken = (await FirebaseAuthentication.getIdToken()).token;
        } else {
          if (!auth.currentUser) {
            throw new Error("Cannot make API request. No user is signed in.");
          }
          idToken = await auth.currentUser.getIdToken(/* forceRefresh */ false);
        }
      } catch (tokenError) {
        // Token refresh can fail when offline (Firebase calls securetoken.googleapis.com)
        if (isOfflineError(tokenError)) {
          showOfflineDialog();
        }
        throw tokenError;
      }

      if (!idToken) {
        throw new Error("Cannot make API request. No ID token available");
      }

      let lastError;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          return await executeRequest(method, path, data, idToken);
        } catch (error) {
          lastError = error;

          // Don't retry if this is the last attempt
          if (attempt === MAX_RETRIES) {
            break;
          }

          // Only retry on retryable errors
          if (!isRetryableError(error)) {
            throw error;
          }

          // Calculate delay and wait before retrying
          const delay = getRetryDelay(attempt);
          console.warn(
            `Request to ${path} failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying in ${Math.round(delay)}ms...`,
          );

          await sleep(delay);

          // Refresh token before retry in case it expired
          try {
            if (Capacitor.isNativePlatform()) {
              idToken = (await FirebaseAuthentication.getIdToken()).token;
            } else {
              idToken = await auth.currentUser.getIdToken(
                /* forceRefresh */ true,
              );
            }
          } catch (tokenRefreshError) {
            // Token refresh failed (likely offline)
            if (isOfflineError(tokenRefreshError)) {
              showOfflineDialog();
            }
            throw tokenRefreshError;
          }
        }
      }

      // All retries exhausted, show offline dialog if applicable
      if (isOfflineError(lastError)) {
        showOfflineDialog();
      }

      throw lastError;
    },
    [executeRequest, showOfflineDialog],
  );

  const get = useCallback((path) => makeRequest("GET", path), [makeRequest]);
  const set = useCallback(
    (path, data) => makeRequest("POST", path, data),
    [makeRequest],
  );
  const remove = useCallback(
    (path) => makeRequest("DELETE", path),
    [makeRequest],
  );
  const update = useCallback(
    (path, data) => makeRequest("PATCH", path, data),
    [makeRequest],
  );
  const put = useCallback(
    (path, data) => makeRequest("PUT", path, data),
    [makeRequest],
  );

  return { get, set, post: set, remove, update, put };
};

export default useApiClient;
